import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, Play, Pause, Grid, Maximize2 } from 'lucide-react';
import { portfolioImages, portfolioCategories } from '../data/mock';
import { ScrollReveal } from './ui/scroll-reveal';
import { Skeleton } from './ui/skeleton-loader';

const PortfolioSection = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const filteredImages = activeCategory === 'all' 
    ? portfolioImages 
    : portfolioImages.filter(img => img.category === activeCategory);

  // Auto-play slider
  useEffect(() => {
    if (!isAutoPlaying || lightboxOpen) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % filteredImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, filteredImages.length, lightboxOpen]);

  const handleImageLoad = (imageId) => {
    setLoadedImages(prev => ({ ...prev, [imageId]: true }));
  };

  const nextImage = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % filteredImages.length);
  }, [filteredImages.length]);

  const prevImage = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
  }, [filteredImages.length]);

  const openLightbox = () => {
    setLightboxOpen(true);
    setIsAutoPlaying(false);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape' && lightboxOpen) closeLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, nextImage, prevImage]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [activeCategory]);

  // Get visible thumbnails (current + 4 more)
  const getVisibleThumbnails = () => {
    const total = filteredImages.length;
    const visible = [];
    for (let i = 0; i < Math.min(6, total); i++) {
      const index = (currentIndex + i) % total;
      visible.push({ ...filteredImages[index], originalIndex: index });
    }
    return visible;
  };

  return (
    <section id="portfolio" className="py-20" style={{ background: 'var(--bg-section)' }}>
      <div className="container">
        {/* Section Header */}
        <ScrollReveal animation="fadeUp">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <span 
                className="inline-block text-xs font-semibold uppercase tracking-widest mb-3"
                style={{ color: 'var(--accent-gold)' }}
              >
                Our Work
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                Project Gallery
              </h2>
            </div>
            
            {/* Category Pills */}
            <div className="flex flex-wrap gap-2">
              {portfolioCategories.slice(0, 5).map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    activeCategory === category.id ? 'text-white shadow-md' : 'hover:bg-white'
                  }`}
                  style={{ 
                    background: activeCategory === category.id ? 'var(--navy-primary)' : 'transparent',
                    border: `1.5px solid ${activeCategory === category.id ? 'transparent' : 'var(--border-primary)'}`,
                    color: activeCategory === category.id ? 'white' : 'var(--text-secondary)'
                  }}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </ScrollReveal>

        {/* Main Gallery */}
        <ScrollReveal animation="fadeUp" delay={100}>
          <div className="grid lg:grid-cols-12 gap-4">
            {/* Main Image */}
            <div className="lg:col-span-8">
              <div 
                className="relative aspect-[4/3] rounded-2xl overflow-hidden group cursor-pointer"
                style={{ boxShadow: '0 20px 50px rgba(26, 62, 92, 0.15)' }}
                onClick={openLightbox}
              >
                {/* Skeleton */}
                {!loadedImages[filteredImages[currentIndex]?.id] && (
                  <div className="absolute inset-0 z-10">
                    <Skeleton className="w-full h-full" />
                  </div>
                )}

                {/* Main Image */}
                <img
                  src={filteredImages[currentIndex]?.src}
                  alt={filteredImages[currentIndex]?.title}
                  className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-105 ${
                    loadedImages[filteredImages[currentIndex]?.id] ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={() => handleImageLoad(filteredImages[currentIndex]?.id)}
                />

                {/* Gradient Overlay */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: 'linear-gradient(to top, rgba(15, 40, 64, 0.8) 0%, transparent 50%)' }}
                />

                {/* Top Controls */}
                <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-10">
                  <div 
                    className="px-3 py-1.5 rounded-lg text-sm font-medium text-white"
                    style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
                  >
                    {currentIndex + 1} / {filteredImages.length}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); setIsAutoPlaying(!isAutoPlaying); }}
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
                      style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
                    >
                      {isAutoPlaying ? <Pause size={16} /> : <Play size={16} />}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); openLightbox(); }}
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
                      style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
                    >
                      <Maximize2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Bottom Info */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                  <p className="font-semibold text-lg">{filteredImages[currentIndex]?.title}</p>
                  <p className="text-white/70 text-sm capitalize">{filteredImages[currentIndex]?.category}</p>
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-xl flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 z-10"
                  style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-xl flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 z-10"
                  style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
                >
                  <ChevronRight size={22} />
                </button>
              </div>
            </div>

            {/* Side Thumbnails */}
            <div className="lg:col-span-4">
              <div className="grid grid-cols-3 lg:grid-cols-2 gap-3 h-full">
                {getVisibleThumbnails().map((image, index) => (
                  <button
                    key={`${image.id}-${index}`}
                    onClick={() => {
                      setCurrentIndex(image.originalIndex);
                      setIsAutoPlaying(false);
                    }}
                    className={`relative aspect-square lg:aspect-[4/3] rounded-xl overflow-hidden transition-all duration-300 ${
                      image.originalIndex === currentIndex 
                        ? 'ring-2 ring-offset-2 scale-[0.98]' 
                        : 'opacity-70 hover:opacity-100 hover:scale-[1.02]'
                    }`}
                    style={{ 
                      ringColor: 'var(--navy-primary)',
                      ringOffsetColor: 'var(--bg-section)'
                    }}
                  >
                    <img
                      src={image.src}
                      alt={image.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {image.originalIndex === currentIndex && (
                      <div className="absolute inset-0 bg-white/20" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* Progress Dots */}
        <ScrollReveal animation="fadeUp" delay={200}>
          <div className="flex justify-center items-center gap-4 mt-8">
            <div className="flex gap-1.5">
              {filteredImages.slice(0, Math.min(filteredImages.length, 10)).map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentIndex(index);
                    setIsAutoPlaying(false);
                  }}
                  className="h-1.5 rounded-full transition-all duration-500"
                  style={{ 
                    width: index === currentIndex ? '28px' : '8px',
                    background: index === currentIndex ? 'var(--navy-primary)' : 'var(--border-primary)'
                  }}
                />
              ))}
            </div>
            {filteredImages.length > 10 && (
              <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                +{filteredImages.length - 10} more
              </span>
            )}
          </div>
        </ScrollReveal>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ background: 'rgba(10, 25, 40, 0.98)' }}
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-5 right-5 z-20 w-11 h-11 rounded-xl flex items-center justify-center text-white/70 hover:text-white transition-all duration-300 hover:bg-white/10"
          >
            <X size={22} />
          </button>

          <div className="absolute top-5 left-5 text-white/70 text-sm font-medium px-4 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.1)' }}>
            {currentIndex + 1} / {filteredImages.length}
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 md:left-8 z-20 w-12 h-12 rounded-xl flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
            style={{ background: 'rgba(255,255,255,0.1)' }}
          >
            <ChevronLeft size={26} />
          </button>

          <div className="relative max-w-5xl max-h-[80vh] mx-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={filteredImages[currentIndex]?.src}
              alt={filteredImages[currentIndex]?.title}
              className="max-w-full max-h-[80vh] object-contain rounded-xl"
            />
            <div className="absolute -bottom-12 left-0 right-0 text-center">
              <p className="text-white font-medium">{filteredImages[currentIndex]?.title}</p>
            </div>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 md:right-8 z-20 w-12 h-12 rounded-xl flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
            style={{ background: 'rgba(255,255,255,0.1)' }}
          >
            <ChevronRight size={26} />
          </button>

          {/* Thumbnails */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto px-4 py-3 rounded-xl scrollbar-hide" style={{ background: 'rgba(0,0,0,0.4)' }}>
            {filteredImages.map((image, index) => (
              <button
                key={image.id}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
                className={`flex-shrink-0 w-14 h-10 rounded-lg overflow-hidden transition-all duration-300 ${
                  index === currentIndex ? 'ring-2 ring-white scale-105' : 'opacity-40 hover:opacity-80'
                }`}
              >
                <img src={image.src} alt={image.title} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default PortfolioSection;
